import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import ItemService from "../../../apis/ItemService";

const getInitialFormState = (typeKey) => ({
  arabicName: "",
  name: "",
  type: typeKey || "",
  active: true,
});

const normaliseTaxonomyEntry = (entry, fallbackType) => ({
  id: entry?.id ?? "",
  arabicName: entry?.arabicName ?? "",
  name: entry?.name ?? "",
  type: entry?.type ?? fallbackType ?? "",
  active: Boolean(entry?.active),
});

const sortTaxonomies = (taxonomies = []) =>
  [...taxonomies].sort((a, b) => {
    const aName = a?.arabicName ?? "";
    const bName = b?.arabicName ?? "";

    return aName.localeCompare(bName, undefined, {
      sensitivity: "base",
      usage: "sort",
      numeric: true,
    });
  });

const TaxonomyManager = ({ currentType }) => {
  const [taxonomies, setTaxonomies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState(() =>
    getInitialFormState(currentType?.key)
  );
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const selectedIdRef = useRef(null);

  const typeKey = currentType?.key ?? "";
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSelectTaxonomy = useCallback((taxonomy) => {
    const normalised = normaliseTaxonomyEntry(taxonomy, typeKey);

    selectedIdRef.current = normalised.id || null;
    setSelectedTaxonomyId(normalised.id || null);
    setEditForm(normalised);
    setFeedback(null);
    setDeletingId(null);
  }, [typeKey]);

  const handleOpenTaxonomyDialog = useCallback(
    (taxonomy) => {
      handleSelectTaxonomy(taxonomy);
      setEditDialogOpen(true);
    },
    [handleSelectTaxonomy]
  );

  const loadTaxonomies = useCallback(
    async (preferredId) => {
      if (!ItemService.isConfigured()) {
        setError("Items API URL is not configured.");
        setTaxonomies([]);
        setEditForm(null);
        setSelectedTaxonomyId(null);
        setEditDialogOpen(false);
        return;
      }

      if (!typeKey) {
        setTaxonomies([]);
        setEditForm(null);
        setSelectedTaxonomyId(null);
        setEditDialogOpen(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await ItemService.fetchTaxonomy(typeKey);
        const sorted = sortTaxonomies(Array.isArray(response) ? response : []);
        setTaxonomies(sorted);

        const fallbackId = preferredId ?? selectedIdRef.current;
        const nextSelection = fallbackId
          ? sorted.find((entry) => entry.id === fallbackId) ?? null
          : null;

        if (nextSelection) {
          handleSelectTaxonomy(nextSelection);
        } else {
          selectedIdRef.current = null;
          setSelectedTaxonomyId(null);
          setEditForm(null);
          setEditDialogOpen(false);
        }
      } catch (err) {
        const message =
          err?.response?.data?.message || err?.message || "Failed to load taxonomy.";
        setError(message);
        setTaxonomies([]);
        selectedIdRef.current = null;
        setSelectedTaxonomyId(null);
        setEditForm(null);
        setEditDialogOpen(false);
      } finally {
        setLoading(false);
      }
    },
    [handleSelectTaxonomy, typeKey]
  );

  useEffect(() => {
    selectedIdRef.current = null;
    setSelectedTaxonomyId(null);
    setEditForm(null);
    setFeedback(null);
    setCreateForm(getInitialFormState(typeKey));
    setEditDialogOpen(false);
    setDeletingId(null);
    loadTaxonomies();
  }, [loadTaxonomies, typeKey]);

  const selectedTaxonomy = useMemo(
    () =>
      taxonomies.find((taxonomy) => taxonomy.id === selectedTaxonomyId) || null,
    [taxonomies, selectedTaxonomyId]
  );

  useEffect(() => {
    if (!selectedTaxonomy) {
      setEditDialogOpen(false);
    }
  }, [selectedTaxonomy]);

  const handleRefresh = () => {
    loadTaxonomies();
  };

  const handleEditFieldChange = (field) => (event) => {
    const value =
      field === "active" ? event.target.checked : event.target.value;

    setEditForm((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!editForm || !selectedTaxonomyId) {
      return;
    }

    if (!editForm.arabicName?.trim() || !editForm.name?.trim()) {
      setFeedback({
        type: "error",
        message: "Please provide both the Arabic and name fields before saving.",
      });
      return;
    }

    setSaving(true);
    setFeedback(null);

    try {
      const payload = {
        id: editForm.id || selectedTaxonomyId || "",
        arabicName: editForm.arabicName.trim(),
        name: editForm.name.trim(),
        type: editForm.type || typeKey,
        active: Boolean(editForm.active),
      };

      await ItemService.updateTaxonomy(selectedTaxonomyId, payload);
      setFeedback({ type: "success", message: "Taxonomy updated successfully." });
      setEditDialogOpen(false);
      await loadTaxonomies(selectedTaxonomyId);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to update taxonomy.";
      setFeedback({ type: "error", message });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateForm(getInitialFormState(typeKey));
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (creating) {
      return;
    }

    setCreateDialogOpen(false);
    setCreateForm(getInitialFormState(typeKey));
    setCreateError(null);
  };

  const handleCreateFieldChange = (field) => (event) => {
    const value =
      field === "active" ? event.target.checked : event.target.value;

    setCreateForm((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    if (!createForm.arabicName?.trim() || !createForm.name?.trim()) {
      setCreateError("Please provide both the Arabic and name fields.");
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      const payload = {
        arabicName: createForm.arabicName.trim(),
        name: createForm.name.trim(),
        type: createForm.type || typeKey,
        active: Boolean(createForm.active),
      };

      const created = await ItemService.createTaxonomy(payload);
      setFeedback({ type: "success", message: "Taxonomy created successfully." });
      setCreateDialogOpen(false);
      setCreateForm(getInitialFormState(typeKey));

      const createdId = created?.id;
      await loadTaxonomies(createdId);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to create taxonomy.";
      setCreateError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleCloseEditDialog = () => {
    if (saving || deletingId) {
      return;
    }

    setEditDialogOpen(false);

    if (selectedTaxonomy) {
      setEditForm(normaliseTaxonomyEntry(selectedTaxonomy, typeKey));
    }
  };

  const handleDeleteTaxonomy = useCallback(
    async (taxonomyId) => {
      if (!taxonomyId || deletingId) {
        return;
      }

      const confirmed = window.confirm(
        "Are you sure you want to delete this taxonomy entry? This action cannot be undone."
      );

      if (!confirmed) {
        return;
      }

      setDeletingId(taxonomyId);
      setFeedback(null);

      try {
        await ItemService.deleteTaxonomy(taxonomyId, typeKey);
        setFeedback({ type: "success", message: "Taxonomy deleted successfully." });

        if (taxonomyId === selectedTaxonomyId) {
          setEditDialogOpen(false);
          selectedIdRef.current = null;
          setSelectedTaxonomyId(null);
          setEditForm(null);
        }

        await loadTaxonomies();
      } catch (err) {
        const message =
          err?.response?.data?.message || err?.message || "Failed to delete taxonomy.";
        setFeedback({ type: "error", message });
      } finally {
        setDeletingId(null);
      }
    },
    [deletingId, loadTaxonomies, selectedTaxonomyId]
  );

  const handleDelete = () => {
    if (!selectedTaxonomyId) {
      return;
    }

    void handleDeleteTaxonomy(selectedTaxonomyId);
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Stack spacing={0.5}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {currentType?.title || "Taxonomy"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentType?.description ||
              "Review and maintain taxonomy entries for this collection."}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Create
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {feedback && (
        <Alert
          severity={feedback.type === "success" ? "success" : "error"}
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      <Card sx={{ display: "flex", flexDirection: "column" }}>
        <CardContent sx={{ flexGrow: 1, p: 0 }}>
          <TableContainer>
            <Table size="small" aria-label="Taxonomy entries">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Arabic Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Loading taxonomy…
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : taxonomies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No taxonomy entries have been added yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  taxonomies.map((taxonomy) => (
                    <TableRow
                      key={taxonomy.id}
                      hover
                      selected={taxonomy.id === selectedTaxonomyId}
                      onClick={() => handleOpenTaxonomyDialog(taxonomy)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{taxonomy.name}</TableCell>
                      <TableCell sx={{ direction: "rtl", textAlign: "right" }}>
                        {taxonomy.arabicName}
                      </TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          variant="body2"
                          color={taxonomy.active ? "success.main" : "text.secondary"}
                          sx={{ fontWeight: 500 }}
                        >
                          {taxonomy.active ? "Active" : "Inactive"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          aria-label="Delete taxonomy"
                          color="error"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDeleteTaxonomy(taxonomy.id);
                          }}
                          disabled={Boolean(deletingId)}
                        >
                          {deletingId === taxonomy.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {!loading && taxonomies.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          Click a taxonomy entry to view its details and make edits.
        </Typography>
      )}

      <Dialog
        open={Boolean(selectedTaxonomy) && editDialogOpen}
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmallScreen}
      >
        <DialogTitle>Edit taxonomy entry</DialogTitle>
        <Box component="form" onSubmit={handleUpdate}>
          <DialogContent dividers>
            <Stack spacing={2}>
              {feedback?.type === "error" && (
                <Alert severity="error">{feedback.message}</Alert>
              )}
              <TextField
                label="ID"
                value={editForm?.id ?? ""}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Arabic name"
                value={editForm?.arabicName ?? ""}
                onChange={handleEditFieldChange("arabicName")}
                required
                fullWidth
                inputProps={{ dir: "rtl", style: { textAlign: "right" } }}
              />
              <TextField
                label="Name"
                value={editForm?.name ?? ""}
                onChange={handleEditFieldChange("name")}
                required
                fullWidth
              />
              <TextField
                label="Type"
                value={editForm?.type ?? typeKey}
                onChange={handleEditFieldChange("type")}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(editForm?.active)}
                    onChange={handleEditFieldChange("active")}
                    color="primary"
                  />
                }
                label={Boolean(editForm?.active) ? "Active" : "Inactive"}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              color="error"
              onClick={handleDelete}
              startIcon={<DeleteIcon />}
              disabled={saving || Boolean(deletingId)}
            >
              {deletingId === selectedTaxonomyId ? "Deleting…" : "Delete"}
            </Button>
            <Button onClick={handleCloseEditDialog} disabled={saving || Boolean(deletingId)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving || Boolean(deletingId)}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} fullWidth>
        <DialogTitle>Create taxonomy entry</DialogTitle>
        <Box component="form" onSubmit={handleCreate}>
          <DialogContent dividers>
            <Stack spacing={2}>
              {createError && <Alert severity="error">{createError}</Alert>}
              <TextField
                label="Arabic name"
                value={createForm.arabicName}
                onChange={handleCreateFieldChange("arabicName")}
                required
                fullWidth
                inputProps={{ dir: "rtl", style: { textAlign: "right" } }}
              />
              <TextField
                label="Name"
                value={createForm.name}
                onChange={handleCreateFieldChange("name")}
                required
                fullWidth
              />
              <TextField
                label="Type"
                value={createForm.type}
                onChange={handleCreateFieldChange("type")}
                fullWidth
                placeholder="arabic"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(createForm.active)}
                    onChange={handleCreateFieldChange("active")}
                    color="primary"
                  />
                }
                label={Boolean(createForm.active) ? "Active" : "Inactive"}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog} disabled={creating}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={creating}
            >
              {creating ? "Creating…" : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Stack>
  );
};

export default TaxonomyManager;
